import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  Link2,
  Play,
  Sparkles,
  Trash2,
  Youtube,
} from "lucide-react";
import DimensionGuide from "./DimensionGuide";
import { IMAGE_GUIDES } from "../../utils/storeDefaults";
import {
  getYouTubeThumbnailUrl,
  parseYouTubeEmbedInput,
  appendYouTubeAutoplay,
} from "../../utils/youtubeEmbed";

const ACCEPTED_FORMATS = [
  "youtube.com/embed/…",
  "youtube.com/watch?v=…",
  "youtu.be/…",
  "youtube.com/shorts/…",
  "Paste iframe embed code",
];

const HighlightVideoEditor = ({
  highlightVideo = {},
  onChange,
  onValidationChange,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(highlightVideo.embedUrl || "");

  useEffect(() => {
    setInputValue(highlightVideo.embedUrl || "");
    setIsPreviewPlaying(false);
  }, [highlightVideo.embedUrl]);

  const parsed = useMemo(() => parseYouTubeEmbedInput(inputValue), [inputValue]);
  const hasInput = Boolean(inputValue.trim());
  const isValid = !hasInput || !parsed.error;
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [thumbQuality, setThumbQuality] = useState("maxresdefault");
  const thumbnail = parsed.videoId ? getYouTubeThumbnailUrl(parsed.videoId, thumbQuality) : "";

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  useEffect(() => {
    setIsPreviewPlaying(false);
    setThumbQuality("maxresdefault");
  }, [parsed.embedUrl]);

  const commitVideo = (nextInput, nextTitle = highlightVideo.title) => {
    const result = parseYouTubeEmbedInput(nextInput);
    onChange({
      embedUrl: result.error ? "" : result.embedUrl,
      title: String(nextTitle || "").trim().slice(0, 80),
    });
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    const result = parseYouTubeEmbedInput(value);
    if (!value.trim()) {
      onChange({ embedUrl: "", title: highlightVideo.title || "" });
      return;
    }
    if (!result.error) {
      onChange({
        embedUrl: result.embedUrl,
        title: highlightVideo.title || "",
      });
    } else {
      onChange({
        embedUrl: "",
        title: highlightVideo.title || "",
      });
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange({ embedUrl: "", title: "" });
  };

  return (
    <div className="space-y-4">
      <DimensionGuide guide={IMAGE_GUIDES.highlightVideo} />

      <div className="flex flex-wrap gap-2">
        {ACCEPTED_FORMATS.map((format) => (
          <span
            key={format}
            className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 rounded-full"
          >
            {format}
          </span>
        ))}
      </div>

      <div>
        <label className="block mb-1.5 text-sm font-medium text-gray-700">
          YouTube embed link
        </label>
        <div className="relative">
          <Link2 className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
          <input
            type="url"
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
            onBlur={() => commitVideo(inputValue, highlightVideo.title)}
            disabled={disabled}
            placeholder="https://www.youtube.com/embed/VIDEO_ID"
            className={`w-full py-3 pl-10 pr-10 text-sm border rounded-2xl focus:outline-none focus:ring-2 disabled:opacity-50 ${
              hasInput && parsed.error
                ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                : hasInput && !parsed.error
                ? "border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100"
                : "border-gray-200 focus:border-[#FF6C6C] focus:ring-[#FF6C6C]/20"
            }`}
          />
          {hasInput ? (
            <span className="absolute -translate-y-1/2 right-3 top-1/2">
              {parsed.error ? (
                <AlertCircle className="w-4 h-4 text-red-500" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              )}
            </span>
          ) : null}
        </div>
        <AnimatePresence mode="wait">
          {hasInput && parsed.error ? (
            <motion.p
              key="error"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-1.5 mt-2 text-xs text-red-600"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {parsed.error}
            </motion.p>
          ) : hasInput && !parsed.error ? (
            <motion.p
              key="success"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-1.5 mt-2 text-xs text-emerald-600"
            >
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              Valid embed — preview updates below.
            </motion.p>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="mt-2 text-xs text-gray-500"
            >
              Only official YouTube video links are accepted. Channel or playlist URLs
              won&apos;t work.
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <div>
        <label className="block mb-1.5 text-sm font-medium text-gray-700">
          Video headline <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={highlightVideo.title || ""}
          onChange={(event) =>
            onChange({
              embedUrl: highlightVideo.embedUrl || "",
              title: event.target.value,
            })
          }
          disabled={disabled || !parsed.embedUrl}
          placeholder="e.g. See our latest collection in action"
          maxLength={80}
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] disabled:opacity-50"
        />
      </div>

      {hasInput ? (
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Remove video
        </button>
      ) : null}

      <AnimatePresence>
        {parsed.embedUrl ? (
          <motion.div
            key={parsed.embedUrl}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="overflow-hidden border border-gray-200 rounded-3xl shadow-lg"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
              <div className="flex items-center gap-2 text-white">
                <Youtube className="w-4 h-4 text-red-400" />
                <span className="text-xs font-semibold tracking-wide uppercase">
                  Live preview
                </span>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-amber-100 rounded-full bg-white/10">
                <Sparkles className="w-3 h-3" />
                1697 × 602 frame
              </span>
            </div>

            <div className="relative w-full aspect-[1697/602] bg-black">
              {!isPreviewPlaying && thumbnail ? (
                <button
                  type="button"
                  onClick={() => setIsPreviewPlaying(true)}
                  className="absolute inset-0 z-10 w-full h-full group"
                  aria-label="Play video preview"
                >
                  <img
                    src={thumbnail}
                    alt=""
                    aria-hidden
                    onError={() => {
                      if (thumbQuality !== "hqdefault") setThumbQuality("hqdefault");
                    }}
                    className="absolute inset-0 object-cover w-full h-full"
                  />
                  <div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/95 shadow-xl transition-transform group-hover:scale-105 text-[#FF6C6C]">
                      <Play className="w-6 h-6 fill-current ml-1" />
                    </span>
                  </div>
                </button>
              ) : null}
              {isPreviewPlaying ? (
                <iframe
                  title={highlightVideo.title || "Store spotlight video preview"}
                  src={appendYouTubeAutoplay(parsed.embedUrl)}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : null}
            </div>

            {highlightVideo.title ? (
              <div className="px-4 py-3 bg-white border-t border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{highlightVideo.title}</p>
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default HighlightVideoEditor;
